import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { 
  Upload, 
  Download, 
  Trash2, 
  FileText, 
  DollarSign, 
  Calendar, 
  Building2,
  Loader2,
  Wand2,
  Plus,
  Undo2,
  Pencil
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Receipts & Documentation Section
 * v2.4: per-receipt auto-sum to actual_cost + refund logging with receipt
 */
export const ReceiptsDocumentation = ({ 
  phaseId, 
  phaseName,
  onTotalChange,
  onAddToActualCost,
  onActualCostChanged,
  isReadOnly = false
}) => {
  const [receipts, setReceipts] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [refundTotal, setRefundTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // v2.4: per-upload "add to actual cost" toggle (default ON)
  const [autoAddNext, setAutoAddNext] = useState(true);
  // Refund dialog state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundData, setRefundData] = useState({ amount: '', vendor: '', reason: 'Wrong part ordered', date: new Date().toISOString().split('T')[0], description: '' });
  const [refundFile, setRefundFile] = useState(null);
  const [isSavingRefund, setIsSavingRefund] = useState(false);
  // Edit-receipt dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editReceipt, setEditReceipt] = useState(null);
  const [editData, setEditData] = useState({ amount: '', vendor: '', date: '', description: '' });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Load receipts for this phase
  const loadReceipts = async () => {
    if (!phaseId) return;
    
    setIsLoading(true);
    try {
      const res = await apiService.getPhaseReceipts(phaseId);
      setReceipts(res.data.receipts || []);
      setTotalAmount(res.data.total_amount || 0);  // receipts only (excludes refunds)
      setRefundTotal(res.data.refund_total || 0);  // refund total tracked separately
      onTotalChange?.(res.data.total_amount || 0);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReceipts();
  }, [phaseId]);

  // Handle file upload with AI extraction
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // First extract data using AI
      const formData = new FormData();
      formData.append('file', file);
      
      let extractedData = {};
      try {
        const extractRes = await apiService.extractReceiptData(formData);
        if (extractRes.data.success && extractRes.data.extracted_data) {
          extractedData = extractRes.data.extracted_data;
        }
      } catch (extractErr) {
        console.error('AI extraction failed, uploading without extracted data:', extractErr);
      }

      // Upload the receipt with extracted data
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('amount', extractedData.total_amount || 0);
      uploadFormData.append('vendor', extractedData.vendor_name || '');
      uploadFormData.append('date', extractedData.date || '');
      uploadFormData.append('description', extractedData.description || 
        (extractedData.line_items ? extractedData.line_items.map(i => i.description).join(', ') : ''));
      // v2.4: include the auto-add flag so the server atomically increments actual_cost
      uploadFormData.append('add_to_actual_cost', autoAddNext ? 'true' : 'false');

      const uploadRes = await apiService.uploadPhaseReceipt(phaseId, uploadFormData);
      
      // If server reports the actual_cost was incremented, notify parent so the
      // edit form reflects the new value without a manual refresh.
      const newActualCost = uploadRes?.data?.actual_cost;
      const wasIncremented = uploadRes?.data?.actual_cost_incremented;
      if (wasIncremented && typeof newActualCost === 'number') {
        onActualCostChanged?.(newActualCost);
        toast.success(`Receipt uploaded — $${(extractedData.total_amount || 0).toLocaleString()} added to actual cost`);
      } else {
        toast.success('Receipt uploaded and data extracted!');
      }
      loadReceipts();
    } catch (err) {
      console.error('Upload failed:', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload receipt');
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Download receipt
  const handleDownload = async (receipt) => {
    try {
      const res = await apiService.downloadPhaseReceipt(phaseId, receipt.id);
      
      // Get content type from response headers or default based on filename
      const contentType = res.headers?.['content-type'] || 'application/octet-stream';
      
      // Determine file extension
      let filename = receipt.filename || receipt.file_name || 'receipt';
      if (!filename.includes('.')) {
        const extMap = {
          'image/jpeg': '.jpg',
          'image/png': '.png', 
          'image/gif': '.gif',
          'image/webp': '.webp',
          'image/heic': '.heic',
          'application/pdf': '.pdf',
        };
        filename += extMap[contentType] || '';
      }
      
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download receipt');
    }
  };

  // Delete receipt
  const handleDelete = async (receiptId) => {
    if (!confirm('Are you sure you want to delete this receipt?')) return;
    
    try {
      await apiService.deletePhaseReceipt(phaseId, receiptId);
      toast.success('Receipt deleted');
      loadReceipts();
    } catch (err) {
      toast.error('Failed to delete receipt');
    }
  };

  // Open the edit dialog for a receipt
  const openEdit = (receipt) => {
    const dateOnly = receipt.date ? String(receipt.date).split('T')[0] : '';
    setEditReceipt(receipt);
    setEditData({
      amount: receipt.amount != null ? String(receipt.amount) : '',
      vendor: receipt.vendor || '',
      date: /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : '',
      description: receipt.description || '',
    });
    setEditOpen(true);
  };

  // Save corrected receipt fields — also fixes Actual Cost by the delta
  const handleSaveEdit = async () => {
    if (!editReceipt) return;
    const amt = parseFloat(editData.amount);
    if (isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setIsSavingEdit(true);
    try {
      const res = await apiService.updatePhaseReceipt(phaseId, editReceipt.id, {
        amount: amt,
        vendor: editData.vendor || '',
        date: editData.date || '',
        description: editData.description || '',
      });
      const newActualCost = res?.data?.actual_cost;
      if (res?.data?.actual_cost_adjusted && typeof newActualCost === 'number') {
        onActualCostChanged?.(newActualCost);
        toast.success('Receipt updated — Actual Cost adjusted');
      } else {
        toast.success('Receipt updated');
      }
      setEditOpen(false);
      setEditReceipt(null);
      loadReceipts();
    } catch (err) {
      console.error('Receipt edit failed:', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update receipt');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Add total to actual cost
  const handleAddToActualCost = () => {
    if (totalAmount > 0) {
      onAddToActualCost?.(totalAmount);
      toast.success(`Added $${totalAmount.toLocaleString()} to Actual Cost`);
    }
  };

  // v2.4: Log a refund (with optional receipt)
  const handleRefundFileSelect = async (e) => {
    const file = e.target.files?.[0] || null;
    setRefundFile(file);
    if (!file) return;

    // Run the same AI OCR as the receipt upload path so amount/vendor/date
    // pre-fill from the credit memo image/PDF.
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiService.extractReceiptData(fd);
      if (res?.data?.success && res.data.extracted_data) {
        const x = res.data.extracted_data;
        setRefundData((prev) => ({
          ...prev,
          amount: prev.amount || (x.total_amount ? String(x.total_amount) : ''),
          vendor: prev.vendor || x.vendor_name || '',
          date: prev.date || (x.date && x.date.match(/^\d{4}-\d{2}-\d{2}$/) ? x.date : prev.date),
          description: prev.description || x.description || '',
        }));
        toast.success('Receipt scanned — amount and vendor auto-filled');
      }
    } catch (err) {
      // OCR failures are non-blocking — user can fill in manually
      console.warn('OCR failed on refund receipt:', err?.message || err);
    }
  };

  const handleSaveRefund = async () => {
    const amt = parseFloat(refundData.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Enter a refund amount greater than $0');
      return;
    }
    setIsSavingRefund(true);
    try {
      const fd = new FormData();
      if (refundFile) fd.append('file', refundFile);
      fd.append('amount', String(amt));
      if (refundData.vendor) fd.append('vendor', refundData.vendor);
      if (refundData.reason) fd.append('reason', refundData.reason);
      if (refundData.date) fd.append('refund_date', refundData.date);
      if (refundData.description) fd.append('description', refundData.description);
      const axios = (await import('axios')).default;
      const res = await axios.post(`${API}/phases/${phaseId}/refunds`, fd);
      toast.success(`Refund logged — effective cost now $${(res.data?.effective_cost || 0).toLocaleString()}`);
      setRefundOpen(false);
      setRefundData({ amount: '', vendor: '', reason: 'Wrong part ordered', date: new Date().toISOString().split('T')[0], description: '' });
      setRefundFile(null);
      loadReceipts();
    } catch (err) {
      console.error('Refund save failed:', err);
      toast.error('Failed to log refund');
    } finally {
      setIsSavingRefund(false);
    }
  };

  // Format date for display — MM/DD/YYYY (matches DrawReady canonical format)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const dateOnly = String(dateStr).split('T')[0];
      const parts = dateOnly.split('-');
      if (parts.length === 3) {
        const month = parts[1].padStart(2, '0');
        const day = parts[2].padStart(2, '0');
        return `${month}/${day}/${parts[0]}`;
      }
      const date = new Date(dateStr);
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      const d = date.getDate().toString().padStart(2, '0');
      return `${m}/${d}/${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-3 border-t pt-4 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-600" />
          <span className="font-medium text-slate-700">Receipts & Documentation</span>
          {receipts.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {receipts.length} receipt{receipts.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Upload Button - Hidden for read-only users */}
      {!isReadOnly && (
        <div className="relative">
          <input
            type="file"
            id={`receipt-upload-${phaseId}`}
            className="hidden"
            accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif"
            onChange={handleUpload}
            disabled={isUploading}
          />
          <label htmlFor={`receipt-upload-${phaseId}`}>
            <Button
              variant="outline"
              size="sm"
              className="w-full cursor-pointer border-dashed"
              disabled={isUploading}
              asChild
            >
              <span>
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading & Extracting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Receipt / Invoice
                  </>
                )}
              </span>
            </Button>
          </label>
          {/* v2.4: per-upload auto-add toggle + refund button */}
          <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="auto-add-actual-cost-toggle">
              <Checkbox
                checked={autoAddNext}
                onCheckedChange={(v) => setAutoAddNext(!!v)}
                className="h-4 w-4"
              />
              <span>Add this receipt&apos;s total to Actual Cost</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRefundOpen(true)}
              className="text-orange-700 hover:text-orange-800 hover:bg-orange-50 h-7 px-2 text-xs"
              data-testid="log-refund-btn"
            >
              <Undo2 className="w-3 h-3 mr-1" />
              Log Refund
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1 text-center">
            <Wand2 className="w-3 h-3 inline mr-1" />
            AI will auto-extract amount, vendor, and date from photos
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      )}

      {/* Receipt Cards */}
      {!isLoading && receipts.length > 0 && (
        <div className="space-y-2">
          {receipts.map((receipt) => {
            const isRefund = receipt.category === 'refund';
            return (
            <Card key={receipt.id} className={`p-3 ${isRefund ? 'bg-orange-50 border-orange-200' : 'bg-slate-50'}`}>
              <div className="flex justify-between items-start">
                <div className="space-y-1 flex-1">
                  {isRefund && (
                    <Badge variant="outline" className="text-xs bg-orange-100 text-orange-800 border-orange-300 mb-1">
                      <Undo2 className="w-3 h-3 mr-1" /> Refund
                    </Badge>
                  )}
                  {/* Amount */}
                  {receipt.amount > 0 && (
                    <div className={`flex items-center gap-1 font-semibold ${isRefund ? 'text-orange-700' : 'text-green-700'}`}>
                      <DollarSign className="w-4 h-4" />
                      {isRefund ? '-' : ''}{receipt.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                  
                  {/* Vendor */}
                  {receipt.vendor && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Building2 className="w-3 h-3" />
                      {receipt.vendor}
                    </div>
                  )}
                  
                  {/* Date */}
                  {receipt.date && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(receipt.date)}
                    </div>
                  )}
                  
                  {/* Description */}
                  {receipt.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {receipt.description}
                    </p>
                  )}
                  {isRefund && receipt.reason && (
                    <p className="text-xs text-orange-700 mt-1">
                      Reason: {receipt.reason}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(receipt)}
                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {!isReadOnly && !isRefund && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(receipt)}
                      className="h-8 w-8 p-0 text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                      title="Edit amount / vendor / date"
                      data-testid={`edit-receipt-btn-${receipt.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {!isReadOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(receipt.id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );})}

          {/* Total and Add to Actual Cost - Hidden for read-only users */}
          {(totalAmount > 0 || refundTotal > 0) && (
            <div className="pt-2 border-t space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  Receipts total: <span className="text-green-700">${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </span>
                {!isReadOnly && totalAmount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddToActualCost}
                    className="text-green-700 border-green-300 hover:bg-green-50"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add to Actual Cost
                  </Button>
                )}
              </div>
              {refundTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    Refunds: <span className="text-orange-700">- ${refundTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </span>
                  <span className="text-xs text-slate-500">tracked separately (does not affect Actual Cost)</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && receipts.length === 0 && (
        <p className="text-xs text-slate-400 text-center py-2">
          No receipts uploaded yet
        </p>
      )}

      {/* Edit receipt dialog — correct AI-extracted amount / vendor / date */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-slate-600" /> Edit receipt
            </DialogTitle>
            <DialogDescription>
              Correct what the scanner read. Changing the amount also updates this item&apos;s Actual Cost.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="$0.00"
                value={editData.amount}
                onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                data-testid="edit-receipt-amount"
              />
            </div>
            <div>
              <Label>Vendor</Label>
              <Input
                placeholder="e.g., Brain Engineering, Inc."
                value={editData.vendor}
                onChange={(e) => setEditData({ ...editData, vendor: e.target.value })}
                data-testid="edit-receipt-vendor"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={editData.date}
                onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                data-testid="edit-receipt-date"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="What this receipt is for…"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={isSavingEdit}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-receipt-edit-btn"
            >
              {isSavingEdit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v2.4 — Log Refund dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-4 h-4 text-orange-600" /> Log Refund
            </DialogTitle>
            <DialogDescription>
              Tracks returns + reduces effective cost. Receipt stored alongside other receipts.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Refund amount</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="$0.00"
                value={refundData.amount}
                onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                data-testid="refund-amount-input"
              />
            </div>

            <div>
              <Label>Vendor</Label>
              <Input
                placeholder="e.g., Ferguson Plumbing — Return RMA #88231"
                value={refundData.vendor}
                onChange={(e) => setRefundData({ ...refundData, vendor: e.target.value })}
              />
            </div>

            <div>
              <Label>Reason</Label>
              <Select
                value={refundData.reason}
                onValueChange={(v) => setRefundData({ ...refundData, reason: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wrong part ordered">Wrong part ordered</SelectItem>
                  <SelectItem value="Damaged on delivery">Damaged on delivery</SelectItem>
                  <SelectItem value="Over-ordered">Over-ordered</SelectItem>
                  <SelectItem value="Vendor credit">Vendor credit</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Refund date</Label>
              <Input
                type="date"
                value={refundData.date}
                onChange={(e) => setRefundData({ ...refundData, date: e.target.value })}
              />
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Anything else worth noting…"
                value={refundData.description}
                onChange={(e) => setRefundData({ ...refundData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Refund receipt / credit memo (optional)</Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif"
                onChange={handleRefundFileSelect}
              />
              {refundFile && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Wand2 className="w-3 h-3" /> {refundFile.name} — AI will scan on upload
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={isSavingRefund}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRefund}
              disabled={isSavingRefund}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="save-refund-btn"
            >
              {isSavingRefund ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsDocumentation;
